import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ShieldCheck, Eye, EyeOff, Stethoscope } from 'lucide-react';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role !== 'admin' && data.role !== 'super_admin') {
        await logout();
        setError('This login is for administrators only. Please use the doctor login.');
        setLoading(false);
        return;
      }
      navigate('/admin');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-950 p-6" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#1A4D2E] flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-semibold text-white">ChestBuddy</h1>
            <p className="text-xs text-stone-400">Admin Control Panel</p>
          </div>
        </div>

        <Card className="border-stone-800 bg-stone-900 shadow-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-heading text-white">Admin Sign In</CardTitle>
            <CardDescription className="text-stone-400">Access restricted to administrators and super admins</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="aemail" className="text-stone-300">Admin Email</Label>
                <Input data-testid="admin-login-email" id="aemail" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@xrayassist.com" required className="mt-1 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500" />
              </div>
              <div>
                <Label htmlFor="apw" className="text-stone-300">Password</Label>
                <div className="relative mt-1">
                  <Input data-testid="admin-login-password" id="apw" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter admin password" required className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && <div data-testid="admin-auth-error" className="text-sm text-red-400 bg-red-950/50 p-3 rounded-lg border border-red-900">{error}</div>}

              <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full bg-[#1A4D2E] hover:bg-[#143B23] text-white rounded-lg h-11 font-medium">
                {loading ? 'Authenticating...' : 'Sign In as Admin'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button onClick={() => navigate('/login')} className="text-sm text-stone-500 hover:text-stone-300 flex items-center justify-center gap-1.5 mx-auto">
                <Stethoscope className="w-3.5 h-3.5" /> Doctor Login
              </button>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-stone-600 text-center mt-6">ChestBuddy: ChestAI GP-Assist — Admin Portal</p>
      </div>
    </div>
  );
}
